/**
 * Planning Engine App — sub-router and tab navigation.
 * Lives at /planning/engine/*
 */

import { useMemo } from 'react';
import { Link, NavLink, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import {
  Zap,
  List,
  BarChart2,
  FileText,
  LayoutDashboard,
  Target,
  TrendingUp,
  GitCompareArrows,
  History,
  Dices,
} from 'lucide-react';
import { PlanningEngineProvider, usePlanningEngine } from './PlanningEngineContext';
import { useItemLinkScope } from '../../../lib/itemLinkScope';
import { EngineInputScopePicker } from './EngineInputScopePicker';
import { EngineScenarioPicker } from './EngineScenarioPicker';
import { useTimePhasedPlanningEnabled } from '../../../features/time-phased-planning/hooks/useTimePhasedPlanningEnabled';
import { ModuleRouteGuard } from '../../../components/guards/ModuleRouteGuard';
import PlanningEngineOverview from './PlanningEngineOverview';
import ScenarioList from './ScenarioList';
import PlanningPrioritiesView from './PlanningPrioritiesView';
import PlanningWorkUnitsView from './PlanningWorkUnitsView';
import PlanningDependenciesView from './PlanningDependenciesView';
import PlanningCapacityView from './PlanningCapacityView';
import TeamParticipationView from './TeamParticipationView';
import PlanOfRecordTable from './PlanOfRecordTable';
import PriorityAchievabilityView from './PriorityAchievabilityView';
import AboveBelowLineView from './AboveBelowLineView';
import EngineSummary from './EngineSummary';
import EngineRunHistoryView from './EngineRunHistoryView';
import MonteCarloView from './MonteCarloView';
import ScenarioComparisonView from './ScenarioComparisonView';
import TimePhasedWorkbench from './TimePhasedWorkbench';
import FundingCostsPage from './FundingCostsPage';

type NavIcon = typeof LayoutDashboard;
type NavItem = { name: string; path: string; icon?: NavIcon };
type InputSection = { key: string; label: string; hint: string; items: NavItem[] };
type NavGroup = {
  key: string;
  name: string;
  icon: NavIcon;
  kind: 'single' | 'stepper' | 'segmented';
  items: NavItem[];
  /** Where the top-level tab link lands, if different from items[0].path. */
  defaultPath?: string;
};

/** Viability (rough-cut) — shared with Launch planning via scenario × scope. */
const VIABILITY_INPUTS: NavItem[] = [
  { name: 'Scenarios', path: 'scenarios' },
  { name: 'Priorities', path: 'priorities' },
  { name: 'Work Units', path: 'work-units' },
  { name: 'Dependencies', path: 'dependencies' },
  { name: 'Resource Budget', path: 'capacity' },
];

/** Launch planning (time-phased execution) — separate capability, same shell. */
const LAUNCH_INPUTS: NavItem[] = [
  { name: 'Participation', path: 'participation' },
  { name: 'Time-Phased', path: 'time-phased' },
  { name: 'Funding & Costs', path: 'funding' },
];

const RESULT_ITEMS: NavItem[] = [
  { name: 'Engine Output', path: 'engine-output', icon: FileText },
  { name: 'Achievability', path: 'achievability', icon: Target },
  { name: 'Above / Below', path: 'above-below', icon: TrendingUp },
  { name: 'Monte Carlo', path: 'monte-carlo', icon: Dices },
  { name: 'Summary', path: 'summary', icon: BarChart2 },
  { name: 'Run History', path: 'run-history', icon: History },
];

const ENGINE_BASE = '/planning/engine';
const hrefForPath = (path: string) => (path ? `${ENGINE_BASE}/${path}` : ENGINE_BASE);

function currentSubPath(pathname: string): string {
  if (!pathname.startsWith(ENGINE_BASE)) return '';
  return pathname.slice(ENGINE_BASE.length).replace(/^\/+/, '');
}

function EngineTabBar() {
  const location = useLocation();
  const timePhasedEnabled = useTimePhasedPlanningEnabled();
  const sub = currentSubPath(location.pathname);

  const inputSections: InputSection[] = useMemo(() => {
    const sections: InputSection[] = [
      {
        key: 'viability',
        label: 'Viability',
        hint: 'Rough-cut',
        items: VIABILITY_INPUTS,
      },
    ];
    if (timePhasedEnabled) {
      sections.push({
        key: 'launch',
        label: 'Launch planning',
        hint: 'Time-phased',
        items: LAUNCH_INPUTS,
      });
    }
    return sections;
  }, [timePhasedEnabled]);

  const navGroups: NavGroup[] = useMemo(
    () => [
      {
        key: 'overview',
        name: 'Overview',
        icon: LayoutDashboard,
        kind: 'single',
        items: [{ name: 'Overview', path: '' }],
      },
      {
        key: 'inputs',
        name: 'Inputs',
        icon: List,
        kind: 'stepper',
        items: inputSections.flatMap((s) => s.items),
        // Scenarios is step 1 in the stepper (you set one up before defining
        // its priorities), but it's not where clicking the "Inputs" tab
        // itself should land — Priorities is the page people actually want.
        defaultPath: 'priorities',
      },
      {
        key: 'results',
        name: 'Viability results',
        icon: BarChart2,
        kind: 'segmented',
        items: RESULT_ITEMS,
      },
      {
        key: 'compare',
        name: 'Compare',
        icon: GitCompareArrows,
        kind: 'single',
        items: [{ name: 'Compare', path: 'compare' }],
      },
    ],
    [inputSections],
  );

  const activeGroup =
    navGroups.find((g) => g.items.some((it) => it.path === sub)) ?? navGroups[0];

  return (
    <div className="border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]">
      {/* Primary group bar — scrolls sideways on a phone, like the rows below it */}
      <div className="flex items-center gap-1 px-4 overflow-x-auto">
        <Zap className="h-4 w-4 text-primary-600 dark:text-primary-400 shrink-0 mr-2" />
        <span className="text-xs font-semibold text-primary-700 dark:text-primary-300 mr-4 shrink-0 uppercase tracking-wider">
          Planning Engine
        </span>
        {navGroups.map((g) => {
          const isActive = g.key === activeGroup.key;
          const count = g.items.length > 1 ? g.items.length : null;
          return (
            <Link
              key={g.key}
              to={hrefForPath(g.defaultPath ?? g.items[0].path)}
              aria-current={isActive ? 'page' : undefined}
              className={`flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                isActive
                  ? 'border-primary-600 text-primary-700 dark:text-primary-300 dark:border-primary-400'
                  : 'border-transparent text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)] hover:border-[var(--rule)] dark:hover:border-[var(--rule)]'
              }`}
            >
              <g.icon className="h-4 w-4" />
              {g.name}
              {count != null && (
                <span
                  className={`ml-0.5 text-[10px] font-mono rounded-full px-1.5 py-px border ${
                    isActive
                      ? 'border-primary-300 text-primary-700 dark:text-primary-300 dark:border-primary-400/40'
                      : 'border-[var(--rule)] text-[var(--text-ter)] dark:border-[var(--rule)] dark:text-[var(--text-dim)]'
                  }`}
                >
                  {count}
                </span>
              )}
            </Link>
          );
        })}
      </div>

      {/* Secondary contextual row */}
      {activeGroup.kind === 'stepper' && (
        <EngineStepperRow sections={inputSections} sub={sub} />
      )}
      {activeGroup.kind === 'segmented' && <EngineSegmentedRow items={activeGroup.items} sub={sub} />}
    </div>
  );
}

/** Inputs — two capability sections that share scenario × scope. */
function EngineStepperRow({ sections, sub }: { sections: InputSection[]; sub: string }) {
  return (
    <div className="flex items-stretch gap-3 px-4 py-2 overflow-x-auto bg-[var(--surface)]/80 dark:bg-[var(--surface)] border-t border-[var(--rule)] dark:border-[var(--rule)]">
      {sections.map((section, si) => (
        <div key={section.key} className="flex items-center gap-2 shrink-0">
          {si > 0 && (
            <div
              className="w-px self-stretch bg-[var(--hover-tint)] dark:bg-[var(--rule)] mx-1"
              aria-hidden
            />
          )}
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline gap-1.5 px-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                {section.label}
              </span>
              <span className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-dim)]">
                {section.hint}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {section.items.map((it, i) => {
                const isActive = it.path === sub;
                return (
                  <div key={it.path} className="flex items-center shrink-0">
                    <NavLink
                      to={hrefForPath(it.path)}
                      className={`flex items-center gap-2 pl-1.5 pr-2.5 py-1  text-[13px] whitespace-nowrap transition-colors ${
                        isActive
                          ? 'text-[var(--text-pri)] dark:text-[var(--text-pri)] font-semibold'
                          : 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)]'
                      }`}
                    >
                      <span
                        className={`grid place-items-center h-5 w-5 rounded-full text-[10px] font-mono font-bold ${
                          isActive
                            ? section.key === 'launch'
                              ? 'bg-[var(--slate)] text-white dark:bg-[var(--slate)]'
                              : 'bg-primary-600 text-white dark:bg-primary-500'
                            : 'bg-[var(--hover-tint)] text-[var(--text-ter)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-dim)]'
                        }`}
                      >
                        {i + 1}
                      </span>
                      {it.name}
                    </NavLink>
                    {i < section.items.length - 1 && (
                      <span className="text-[var(--text-ter)] dark:text-[var(--text-dim)] px-0.5" aria-hidden>
                        ›
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Results — one engine run, four lenses in a segmented control. */
function EngineSegmentedRow({ items, sub }: { items: NavItem[]; sub: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 overflow-x-auto bg-[var(--surface)]/80 dark:bg-[var(--surface)] border-t border-[var(--rule)] dark:border-[var(--rule)]">
      <div className="inline-flex items-center gap-1 border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] p-1 shrink-0">
        {items.map((it) => {
          const isActive = it.path === sub;
          const Icon = it.icon;
          return (
            <NavLink
              key={it.path}
              to={hrefForPath(it.path)}
              className={`flex items-center gap-1.5 px-3 py-1  text-[13px] whitespace-nowrap transition-colors ${
                isActive
                  ? 'bg-[var(--hover-tint)] dark:bg-[var(--active-tint)] text-[var(--text-pri)] dark:text-[var(--text-pri)] font-semibold shadow-sm'
                  : 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri)]'
              }`}
            >
              {Icon && <Icon className="h-3.5 w-3.5" />}
              {it.name}
            </NavLink>
          );
        })}
      </div>
      <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] italic hidden sm:inline">
        Viability lenses on one rough-cut run
      </span>
    </div>
  );
}

function EngineScopeBar() {
  const {
    engineInput,
    setEngineInput,
    scopedItemIds,
    activeScenario,
    activeRunUpdatedAt,
    planOutput,
    hardScoped,
  } = usePlanningEngine();

  // scenario_portfolio scope counts priority rows, not items.
  const scopeNoun =
    engineInput.type === 'scenario_portfolio'
      ? scopedItemIds?.size === 1
        ? 'priority'
        : 'priorities'
      : scopedItemIds?.size === 1
        ? 'item'
        : 'items';
  const countLabel =
    scopedItemIds == null ? 'All live items' : `${scopedItemIds.size} ${scopeNoun} in scope`;

  // A non-empty scope that produced zero rows is a defect signal, never a status line.
  const emptyRunDefect =
    planOutput != null &&
    scopedItemIds != null &&
    scopedItemIds.size > 0 &&
    planOutput.executiveSummary.totalWorkUnits === 0;

  const instanceStatus = !activeScenario
    ? 'Select a scenario to create an engine instance'
    : activeRunUpdatedAt
      ? `Instance saved · last run ${new Date(activeRunUpdatedAt).toLocaleString()}`
      : 'This Scenario × Scope has never been run — click Run to save an instance';

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)]/80 dark:bg-[var(--bg)]">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-sec)] shrink-0">
        Scenario
      </span>
      <EngineScenarioPicker />

      <span className="text-[var(--text-ter)] dark:text-[var(--rule-bright)] hidden sm:inline" aria-hidden>
        ×
      </span>

      <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-sec)] shrink-0">
        Input Scope
      </span>
      <EngineInputScopePicker
        current={engineInput}
        onChange={setEngineInput}
        variant="compact"
        lockLive={hardScoped}
      />

      {emptyRunDefect ? (
        <span
          className="text-xs font-medium text-[var(--coral)] dark:text-[var(--coral)] truncate"
          title="The scope resolved members but the engine produced no rows — the scope's contents did not reach the run. Re-run after checking the scope, or report this."
        >
          {countLabel} · run produced 0 rows — scope did not reach the engine
        </span>
      ) : (
        <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] truncate">
          {countLabel}
          {planOutput ? ` · ${planOutput.executiveSummary.totalWorkUnits} rows in instance` : ''}
        </span>
      )}
      <span
        className={`text-xs truncate ml-auto ${
          activeRunUpdatedAt
            ? 'text-[var(--green)] dark:text-[var(--text-ter)]'
            : 'text-[var(--mustard)] dark:text-[var(--mustard)]'
        }`}
      >
        {instanceStatus}
      </span>
    </div>
  );
}

/**
 * A scenario working on a plan says so on every item link the engine draws (roadmap
 * S-37), so the item page opens on that plan's rank and lock. A what-if scenario names
 * no plan, and its links stay plain.
 */
function EngineItemLinkScope() {
  const { activeScenario } = usePlanningEngine();
  useItemLinkScope({ planCycleId: activeScenario?.planCycleId ?? null });
  return null;
}

export default function PlanningEngineApp() {
  return (
    <PlanningEngineProvider>
      <div className="flex flex-col h-full">
        <EngineTabBar />
        <EngineItemLinkScope />
        <EngineScopeBar />
        <div className="flex-1 overflow-hidden px-2 md:px-4 pb-4 pt-2 min-h-0">
          <Routes>
            <Route index element={<PlanningEngineOverview />} />
            <Route path="scenarios" element={<ScenarioList />} />
            <Route path="priorities" element={<PlanningPrioritiesView />} />
            <Route path="work-units" element={<PlanningWorkUnitsView />} />
            <Route path="dependencies" element={<PlanningDependenciesView />} />
            <Route path="capacity" element={<PlanningCapacityView />} />
            {/* Launch planning (time_phased_planning): Time-Phased, Funding & Costs and the
                Launch Calendar leave through the module guard when it is off; so does
                Participation, which the tab bar already hides with them. */}
            <Route
              path="participation"
              element={
                <ModuleRouteGuard moduleKey="time_phased_planning" redirectTo="/planning/engine">
                  <TeamParticipationView />
                </ModuleRouteGuard>
              }
            />
            <Route path="time-phased" element={<TimePhasedWorkbench />} />
            <Route path="funding" element={<FundingCostsPage />} />
            <Route path="engine-output" element={<PlanOfRecordTable />} />
            <Route path="achievability" element={<PriorityAchievabilityView />} />
            <Route path="above-below" element={<AboveBelowLineView />} />
            <Route path="monte-carlo" element={<MonteCarloView />} />
            <Route path="compare" element={<ScenarioComparisonView />} />
            <Route path="summary" element={<EngineSummary />} />
            <Route path="run-history" element={<EngineRunHistoryView />} />
            {/* Legacy aliases — "Plan of Record" is a specific, governed artifact
                (authorize_por / plan_of_record_snapshots) elsewhere in this app;
                this tab is the engine's own pre-authorization output, never that.
                Old links/bookmarks to either alias still land on the real page. */}
            <Route path="plan-of-record" element={<Navigate to="/planning/engine/engine-output" replace />} />
            <Route path="output" element={<Navigate to="/planning/engine/engine-output" replace />} />
            <Route path="*" element={<Navigate to="/planning/engine" replace />} />
          </Routes>
        </div>
      </div>
    </PlanningEngineProvider>
  );
}
